'use client'

import { useCallback, useEffect, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Trophy,
  ArrowUpRight,
  Ticket,
  Users,
  CalendarClock,
  MapPin,
  Swords,
  Monitor,
  UsersRound,
  Shield,
  ExternalLink,
  Settings,
  MessageCircle,
  Lock,
} from 'lucide-react'
import {
  type Tournament,
  formatMoney,
  formatPrizePool,
  formatDate,
  formatTime,
  zoneAbbr,
  relativeDay,
  participantNoun,
  playerCapacity,
  hasTeamMap,
} from '@/lib/tournaments'
import { cn } from '@/lib/utils'
import { useLocalTimezone } from '@/lib/use-local-timezone'
import {
  getRegistrationStanding,
  type RegistrationStanding,
} from '@/lib/tournament-registration'
import { hasBracket } from '@/lib/tournament-bracket'
import { BracketPreviewSection } from './bracket-view'
import {
  Section,
  BulletList,
  SecondaryActions,
  trackExternalTournamentClick,
} from './detail-common'
import { useAuth } from '@/contexts/AuthContext'
import { JoinTournamentButton } from './join-button'
import { useCanManageTournament } from '@/lib/use-can-manage-tournament'
import { InviteBanner } from './invites-section'
import { AddToCalendarButton } from './add-to-calendar-button'
import { StreamPlayer } from './stream-player'
import { useLobbyCode, LobbyCodeCard, LobbyCodeBar } from './lobby-code'
import { TournamentActivity } from './tournament-activity'
import { TournamentParticipants } from './tournament-participants'
import { TeamMapPlayerCard } from './team-map-player'
import { TournamentHero } from './tournament-hero'
import { EventDescription, type RenderedDescription } from './event-description'
import { OrgSocialLinks } from '@/components/organizations/org-social-links'
import type { SocialLink } from '@/lib/social-platforms'

export function TournamentDetail({
  t,
  description,
  links,
}: {
  t: Tournament
  /** Rendered on the server by lib/rich-text, which stays out of the bundle. */
  description: RenderedDescription
  /** Organizer links, merged from every source on the server. */
  links: SocialLink[]
}) {
  // Show start times against the viewer's own clock, falling back to the
  // event's stored zone until the local zone resolves after mount.
  const localTz = useLocalTimezone()
  const displayTz = localTz ?? t.timezone
  const pathname = usePathname()
  const localeSeg = pathname?.split('/')[1]
  const locale = localeSeg === 'es' ? 'es' : localeSeg === 'ja' ? 'ja' : 'en'

  // Organizers (and GamerPlug staff, for GamerPlug-created events) see a
  // management entry point above the stat bar.
  const { user } = useAuth()
  const { canManage: isOrganizer } = useCanManageTournament(t)

  // Live registration standing (real counts + this user's status), fetched via
  // the SECURITY DEFINER RPC since the table's RLS hides other users' rows.
  const [standing, setStanding] = useState<RegistrationStanding | null>(null)
  const loadStanding = useCallback(async () => {
    setStanding(await getRegistrationStanding(t.id))
  }, [t.id])
  useEffect(() => {
    loadStanding()
  }, [loadStanding])

  // ?join=1 comes from a list card's "Join": start joining once the standing
  // has loaded. Read from window (not useSearchParams) so the statically
  // rendered page needs no Suspense boundary, and drop it from the address
  // bar right away so a refresh after leaving doesn't join again.
  const [joinRequested, setJoinRequested] = useState(false)
  useEffect(() => {
    const url = new URL(window.location.href)
    if (url.searchParams.get('join') !== '1') return
    setJoinRequested(true)
    url.searchParams.delete('join')
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash)
  }, [])

  const capacity = standing?.capacity ?? playerCapacity(t)
  const confirmed = standing?.confirmed ?? 0
  const substitutes = standing?.substitutes ?? 0
  const myStatus = standing?.myStatus ?? 'none'
  const atCapacity = confirmed >= capacity
  const spotsRemaining = Math.max(0, capacity - confirmed)
  const pct = capacity > 0 ? Math.min(100, Math.round((confirmed / capacity) * 100)) : 0

  // Externally sourced events (e.g. Challengermode) aren't registered through
  // us, so registration counts are meaningless — hide them.
  const isExternal = !!t.source

  // Hosted bracket (beta): upgrades the completed-state CTA to our own
  // bracket page (the in-page preview section does its own fetch).
  const [hasHostedBracket, setHasHostedBracket] = useState(false)
  useEffect(() => {
    hasBracket(t.id).then(setHasHostedBracket)
  }, [t.id])

  // Bumped on join/leave so the activity feed re-resolves chat + membership.
  const [feedSignal, setFeedSignal] = useState(0)

  // Lobby code: mirror it into a sticky sidebar card once the in-feed card
  // scrolls out of view above the viewport.
  const lobby = useLobbyCode(t.id, t.startsAt)
  const [lobbyPinned, setLobbyPinned] = useState(false)

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Below lg there is no sticky sidebar, so pin a slim bar under the
          navbar while the in-feed lobby card is scrolled out of view. */}
      <AnimatePresence initial={false}>
        {lobbyPinned && lobby.isMember && t.status !== 'completed' && (
          <motion.div
            key="lobby-bar"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-x-0 top-16 z-40 lg:hidden"
          >
            <LobbyCodeBar lobbyCode={lobby.lobbyCode} unlocked={lobby.unlocked} banned={lobby.banned} startsAt={t.startsAt} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Full-bleed immersive hero */}
      <TournamentHero t={t} locale={locale} />

      {/* Floating glass stat bar */}
      <div className="relative mx-auto -mt-32 max-w-5xl px-4">
        {/* Pending invite → Accept / Decline in place of the usual CTA flow. */}
        {!isOrganizer && (
          <InviteBanner
            tournamentId={t.id}
            signedIn={!!user}
            onResponded={() => {
              loadStanding()
              setFeedSignal((n) => n + 1)
            }}
          />
        )}
        {/* Private notice: the event is viewable by all, but taking a spot is
            gated by the organizer's password. */}
        {t.visibility === 'private' && (
          <div className="flex items-center gap-2 rounded-t-3xl border border-white/10 bg-card/60 px-6 py-3 text-sm text-muted-foreground backdrop-blur-xl">
            <Lock className="size-4 shrink-0" />
            <span>
              Private event
              {t.hasPassword
                ? ' — you need the organizer\u2019s password to join.'
                : '.'}
            </span>
          </div>
        )}
        {isOrganizer && (
          <Link
            href={`/${locale}/tournaments/${t.slug}/manage`}
            className={`flex items-center justify-between gap-3 border border-white/10 bg-card/60 px-6 py-4 backdrop-blur-xl transition-colors hover:bg-card/80 ${
              t.visibility === 'private' ? 'border-t-0' : 'rounded-t-3xl'
            }`}
          >
            <span className="text-sm font-medium">
              You&apos;re organizing this event
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-primary/90">
              <Settings className="size-4" /> Manage
            </span>
          </Link>
        )}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={`border border-white/10 bg-card/80 p-6 shadow-2xl backdrop-blur-xl ${
            isOrganizer || t.visibility === 'private'
              ? 'rounded-b-3xl border-t-0'
              : 'rounded-3xl'
          }`}
        >
          <div className={`grid gap-6 ${isExternal ? 'grid-cols-3' : 'grid-cols-2 sm:grid-cols-4'}`}>
            <GlassStat icon={Trophy} label="Prize pool" value={formatPrizePool(t.prizePoolCents, t.currency)} />
            <GlassStat icon={CalendarClock} label="Starts" value={formatDate(t.startsAt, { weekday: 'short' }, displayTz)} />
            <GlassStat icon={Swords} label="Format" value={t.format} />
            {!isExternal && (
              <GlassStat icon={Users} label="Registered" value={`${confirmed}/${capacity}`} />
            )}
          </div>

          <div className="mt-6 border-t border-border pt-5">
            {!isExternal && (
              <>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>
                    {confirmed}/{capacity} players ·{' '}
                    {atCapacity ? 'Spots full' : `${spotsRemaining} spots left`}
                    {substitutes > 0 && ` · ${substitutes} substitute${substitutes === 1 ? '' : 's'}`}
                  </span>
                  <span>{pct}% full</span>
                </div>
                <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-secondary">
                  <div
                    className="h-full rounded-full bg-primary transition-[width] duration-500 ease-out"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </>
            )}

            <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <JoinTournamentButton
                t={t}
                className="w-full sm:w-auto"
                joinAsSubstitute={atCapacity}
                standing={myStatus}
                hasBracket={hasHostedBracket}
                autoStart={joinRequested && standing !== null}
                onMembershipChange={() => {
                  setFeedSignal((s) => s + 1)
                  loadStanding()
                }}
              />
              <div className="flex flex-wrap gap-2">
                {myStatus !== 'none' && <AddToCalendarButton t={t} />}
                <SecondaryActions t={t} />
              </div>
            </div>
            {myStatus === 'substitute' && (
              <p className="mt-3 text-xs text-amber-300">
                You&apos;re registered as a substitute — you&apos;ll be promoted to a
                confirmed spot if one opens up.
              </p>
            )}
            {atCapacity && myStatus === 'none' && t.status !== 'completed' && (
              <p className="mt-3 text-xs text-amber-300">
                All {capacity} spots are taken — you can still join as a substitute.
              </p>
            )}
          </div>
        </motion.div>
      </div>

      {/* Body */}
      <div className="mx-auto max-w-5xl px-4 py-12">
        <div className="grid gap-10 lg:grid-cols-[1fr_300px]">
          <div className="space-y-10">
            {/* Organizer's stream, while the event is actually running. The
                status is server-derived (status_effective), so this flips on
                and off without anyone toggling a flag. */}
            {t.status === 'live' && <StreamPlayer t={t} />}

            <Section title="About this tournament">
              <div className="space-y-5">
                <OrgSocialLinks socials={links} className="justify-start" />
                <EventDescription {...description} />
              </div>
            </Section>

            {/* Apex-only pair: the viewer's seat and the RP standings, side by
                side on desktop. The team card retires once the event is over;
                the standings stay useful, and then take the full width. */}
            {!isExternal && hasTeamMap(t.gameSlug) && (
              <div
                className={cn(
                  'grid gap-4',
                  t.status !== 'completed' && 'md:grid-cols-2 md:items-stretch'
                )}
              >
                {t.status !== 'completed' && (
                  <TeamMapPlayerCard
                    tournamentId={t.id}
                    startsAt={t.startsAt}
                    isRegistered={myStatus !== 'none'}
                    refreshSignal={feedSignal}
                    slug={t.slug}
                    locale={locale}
                  />
                )}

                <Link
                  href={`/${locale}/tournaments/${t.slug}/leaderboard`}
                  className="group flex flex-col rounded-2xl border border-border bg-card p-5 transition-colors hover:bg-white/5"
                >
                  <span className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    <Trophy className="size-4" />
                    Leaderboard
                  </span>
                  <span className="mt-3 text-sm text-muted-foreground">
                    Every registrant ranked by Apex ranked points.
                  </span>
                  <span className="mt-auto flex items-center justify-center gap-1.5 rounded-xl border border-border bg-background px-3 py-2.5 text-sm font-semibold transition-colors group-hover:bg-white/5">
                    View standings
                    <ArrowUpRight className="size-4" />
                  </span>
                </Link>
              </div>
            )}

            <TournamentActivity
              tournamentId={t.id}
              startsAt={t.startsAt}
              refreshSignal={feedSignal}
              onLobbyPinnedChange={setLobbyPinned}
              completed={t.status === 'completed'}
            />

            {/* Bracket mini-map (renders nothing until a bracket exists). */}
            <BracketPreviewSection
              tournamentId={t.id}
              slug={t.slug}
              locale={locale}
            />

            {(t.rules.length > 0 || t.requirements.length > 0) && (
              <div className="grid gap-8 sm:grid-cols-2">
                {t.rules.length > 0 && (
                  <Section title="Rules">
                    <BulletList items={t.rules} />
                  </Section>
                )}
                {t.requirements.length > 0 && (
                  <Section title="Requirements">
                    <BulletList items={t.requirements} />
                  </Section>
                )}
              </div>
            )}
          </div>

          {/* side info card */}
          <aside className="space-y-5 lg:sticky lg:top-24 lg:self-start">
            <AnimatePresence initial={false}>
              {lobbyPinned && lobby.isMember && t.status !== 'completed' && (
                <motion.div
                  key="sticky-lobby"
                  initial={{ opacity: 0, y: -14, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: 'auto' }}
                  exit={{ opacity: 0, y: -14, height: 0 }}
                  transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                  className="hidden overflow-hidden lg:block"
                >
                  <LobbyCodeCard lobbyCode={lobby.lobbyCode} unlocked={lobby.unlocked} banned={lobby.banned} startsAt={t.startsAt} />
                </motion.div>
              )}
            </AnimatePresence>
            {!isExternal && (
              <TournamentParticipants
                confirmed={confirmed}
                capacity={capacity}
                substitutes={substitutes}
                refreshSignal={feedSignal}
                tournamentId={t.id}
                gameSlug={t.gameSlug}
              />
            )}

            <div className="rounded-2xl border border-border bg-card p-5">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Details
              </h3>
              <dl className="mt-4 space-y-3 text-sm">
                <Row icon={CalendarClock} label="Starts">
                  <div className="whitespace-nowrap">
                    {formatDate(t.startsAt, {
                      weekday: 'short',
                      year: 'numeric',
                    }, displayTz)}
                  </div>
                  <div className="whitespace-nowrap">
                    {formatTime(t.startsAt, displayTz)}{' '}
                    <span className="text-muted-foreground">
                      {zoneAbbr(t.startsAt, displayTz)}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {relativeDay(t.startsAt, displayTz)}
                  </div>
                </Row>
                <Row icon={Ticket} label="Entry fee">
                  {formatMoney(t.entryFeeCents, t.currency)}
                </Row>
                <Row icon={MapPin} label="Region">{t.region}</Row>
                {t.eligibility && (
                  <Row icon={Shield} label="Eligibility">{t.eligibility}</Row>
                )}
                <Row icon={Monitor} label="Platform">{t.platform}</Row>
                <Row icon={UsersRound} label="Team size">
                  {t.teamSize === 1 ? 'Solo' : `${t.teamSize} players`}
                </Row>
                {/* Imported events often advertise no cap (Battlefy leaves
                    teamCap/maxPlayers null). 0 means "unknown", so omit the
                    row rather than claiming the event holds 0 teams. */}
                {t.maxParticipants > 0 && (
                  <Row icon={Users} label="Max">
                    {t.maxParticipants} {participantNoun(t)}
                  </Row>
                )}
              </dl>

              {t.externalRegistrationUrl && (
                <div className="mt-5 space-y-2 border-t border-border pt-5">
                  <a
                    href={t.externalRegistrationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      trackExternalTournamentClick(t, 'organizer_page', t.externalRegistrationUrl)
                    }
                    className="flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <ExternalLink className="size-4" /> Organizer page
                  </a>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>

      {/* Jump to the event's group chat. Members only — non-members can read the
          activity feed here but can't open the chat. */}
      {lobby.isMember && lobby.chatId && (
        <Link
          href={`/${locale}/app/matches/group/${lobby.chatId}`}
          aria-label="Open event chat"
          className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3.5 text-sm font-semibold text-white shadow-lg shadow-primary/30 transition hover:brightness-110 active:scale-95"
        >
          <MessageCircle className="size-4" />
          <span className="hidden sm:inline">Event chat</span>
        </Link>
      )}
    </div>
  )
}

function GlassStat({
  icon: Icon,
  label,
  value,
  highlight,
}: {
  icon: typeof Trophy
  label: string
  value: string
  highlight?: boolean
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground">
        <Icon className="size-3.5" />
        {label}
      </div>
      <div className={`mt-1 text-2xl font-bold ${highlight ? 'text-primary' : ''}`}>
        {value}
      </div>
    </div>
  )
}

function Row({
  icon: Icon,
  label,
  children,
}: {
  icon: typeof Trophy
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="flex items-center gap-1.5 text-muted-foreground">
        <Icon className="size-4" />
        {label}
      </dt>
      <dd className="text-right font-medium">{children}</dd>
    </div>
  )
}
